import { requireService, wrap } from '@repo/di-ez';
import { AddRoundLight, CloseRoundLight, DoneRoundLight } from '@repo/go-icons';
import { Button, Drawer, Loader, Scrollbar } from '@repo/go-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrajectoryUomOptionsStore } from '../../utils/trajectory-uom-options';

import { TrajectoryDetails } from './trajectory-details';
import { TrajectoryListItem } from './trajectory-list-item';
import styles from './trajectory-list-sidebar.module.scss';
import { TrajectoryListSidebarStore } from './trajectory-list-sidebar.store';

type Props = {
  isEditing?: boolean;
};

export const TrajectoryListSidebar = wrap<Props>(function TrajectoryListSidebar({ isEditing = false }) {
  const { t } = useTranslation();
  const uiStore = requireService('trajectoryUiStore');
  const trajectoryService = requireService('trajectoryService');
  const notifications = requireService('trajectoryNotifications');
  const directories = requireService('trajectoryDirectories');
  const preferences = requireService('trajectoryPreferences');
  const [uomOptions] = useState(() => new TrajectoryUomOptionsStore(directories));
  const [store] = useState(
    () => new TrajectoryListSidebarStore(trajectoryService, uiStore, notifications, uomOptions, preferences),
  );

  useEffect(() => store.init(), [store]);

  useEffect(() => {
    store.setIsEditing(isEditing);
  }, [isEditing, store]);

  const preview = store.previewTrajectory;
  const parentTitle = store.getParentTrajectoryTitle(preview?.data.parentTrajectoryId);

  return (
    <Drawer
      open={uiStore.isTrajectoryListOpen}
      onClose={store.close}
      style={{ width: 590 }}
      title={t('ListSidebar.title')}
      container={uiStore.settingsDrawerContainer ?? undefined}
      className={styles.drawer}
    >
      <div className={styles.body}>
        <div className={styles.listPane}>
          <div className={styles.list}>
            {store.isLoading && store.trajectories.length === 0 ? (
              <div className={styles.loader}>
                <Loader />
              </div>
            ) : (
              store.trajectories.map((trajectory) => (
                <TrajectoryListItem
                  key={trajectory.id}
                  trajectory={trajectory}
                  selected={store.previewTrajectoryId === trajectory.id}
                  disabled={store.isListLocked && store.previewTrajectoryId !== trajectory.id}
                  canDelete={store.canDeleteTrajectory(trajectory.id)}
                  onSelect={store.setPreviewTrajectoryId}
                  onDelete={(trajectoryId) => void store.deleteTrajectory(trajectoryId)}
                />
              ))
            )}
          </div>
          {isEditing && (
            <div className={styles.listFooter}>
              <Button
                className={styles.addButton}
                size="s"
                icon={<AddRoundLight />}
                disabled={!store.canAddTrajectory}
                onClick={store.addLocalTrajectory}
              >
                {t('ListSidebar.addVersion')}
              </Button>
            </div>
          )}
        </div>

        <div className={styles.detailsPane}>
          <Scrollbar className={styles.detailsScroll}>
            <TrajectoryDetails
              store={store}
              trajectory={preview}
              parentTitle={parentTitle}
              isEditing={isEditing}
              disabled={store.isSaving}
            />
          </Scrollbar>
          {isEditing && store.hasChanges && (
            <div className={styles.footer}>
              <Button
                className={styles.footerButton}
                size="s"
                variant="danger"
                icon={<CloseRoundLight />}
                disabled={!store.canCancel || store.isSaving}
                onClick={store.cancelChanges}
              >
                {t('ListSidebar.cancel')}
              </Button>
              <Button
                className={styles.footerButton}
                size="s"
                variant="success"
                icon={<DoneRoundLight />}
                disabled={!store.canApply}
                loading={store.isSaving}
                onClick={() => void store.apply()}
              >
                {t('ListSidebar.apply')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
});
